import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import PageLayout from '../../components/PageLayout';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../config/firebase';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { checkEntityReferences } from '../../utils/deletionSafeguard';
import { colors, spacing, borderRadius, shadows, typography } from '../../theme/colors';
import {
  FiPlus, FiEdit2, FiTrash2, FiMapPin, FiSearch, FiX,
  FiChevronDown, FiChevronRight, FiMap, FiNavigation
} from 'react-icons/fi';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import usePagePermissions from '../../hooks/usePagePermissions';
import useEscapeClose from '../../hooks/useEscapeClose';

const Regions = () => {
  const { activeCompany } = useAuth();
  const { canEdit, canDelete, canChange } = usePagePermissions('masterData', 'regions');
  const [regions, setRegions] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRegions, setExpandedRegions] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  // Modal states
  const [showRegionModal, setShowRegionModal] = useState(false);
  useEscapeClose(showRegionModal, () => setShowRegionModal(false));
  const [showAreaModal, setShowAreaModal] = useState(false);
  useEscapeClose(showAreaModal, () => setShowAreaModal(false));
  const [editingRegion, setEditingRegion] = useState(null);
  const [editingArea, setEditingArea] = useState(null);
  const [selectedRegionId, setSelectedRegionId] = useState(null);

  // Form data
  const [regionForm, setRegionForm] = useState({ regionCode: '', regionName: '', description: '' });
  const [areaForm, setAreaForm] = useState({ areaCode: '', areaName: '', regionId: '', description: '' });

  useEffect(() => {
    if (activeCompany?.id) {
      fetchAllData();
    }
  }, [activeCompany]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchRegions(), fetchAreas()]);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  const fetchRegions = async () => {
    const q = query(
      collection(db, `companies/${activeCompany.id}/regions`),
      orderBy('regionCode')
    );
    const snapshot = await getDocs(q);
    setRegions(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  };

  const fetchAreas = async () => {
    const q = query(
      collection(db, `companies/${activeCompany.id}/areas`),
      orderBy('areaCode')
    );
    const snapshot = await getDocs(q);
    setAreas(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  };

  const generateRegionCode = () => {
    const maxCode = regions.reduce((max, reg) => {
      const num = parseInt(reg.regionCode.replace('REG-', '')) || 0;
      return num > max ? num : max;
    }, 0);
    return `REG-${String(maxCode + 1).padStart(3, '0')}`;
  };

  const generateAreaCode = () => {
    const maxCode = areas.reduce((max, area) => {
      const num = parseInt(area.areaCode.replace('AREA-', '')) || 0;
      return num > max ? num : max;
    }, 0);
    return `AREA-${String(maxCode + 1).padStart(3, '0')}`;
  };

  // Region handlers
  const handleAddRegion = () => {
    setRegionForm({ regionCode: generateRegionCode(), regionName: '', description: '' });
    setEditingRegion(null);
    setShowRegionModal(true);
  };

  const handleEditRegion = (region) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    setRegionForm({
      regionCode: region.regionCode,
      regionName: region.regionName,
      description: region.description || ''
    });
    setEditingRegion(region);
    setShowRegionModal(true);
  };

  const handleSaveRegion = async () => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (!regionForm.regionName.trim()) {
      alert('Region name is required');
      return;
    }

    try {
      if (editingRegion) {
        await updateDoc(doc(db, `companies/${activeCompany.id}/regions`, editingRegion.id), {
          ...regionForm,
          updatedAt: serverTimestamp()
        });
      } else {
        await addDoc(collection(db, `companies/${activeCompany.id}/regions`), {
          ...regionForm,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
      setShowRegionModal(false);
      fetchRegions();
    } catch (error) {
      console.error('Error saving region:', error);
      alert('Failed to save region');
    }
  };

  const handleDeleteRegion = async (region) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    const linkedAreas = areas.filter(area => area.regionId === region.id);
    if (linkedAreas.length > 0) {
      alert('Cannot delete region with areas. Please delete areas first.');
      return;
    }

    if (window.confirm(`Are you sure you want to delete "${region.regionName}"?`)) {
      try {
        const { hasReferences, message } = await checkEntityReferences(activeCompany?.id, 'region', region.id);
        if (hasReferences) { alert(message); return; }
        await deleteDoc(doc(db, `companies/${activeCompany.id}/regions`, region.id));
        fetchRegions();
      } catch (error) {
        console.error('Error deleting region:', error);
        alert('Failed to delete region');
      }
    }
  };

  // Area handlers
  const handleAddArea = (regionId) => {
    setAreaForm({
      areaCode: generateAreaCode(),
      areaName: '',
      regionId: regionId,
      description: ''
    });
    setEditingArea(null);
    setSelectedRegionId(regionId);
    setShowAreaModal(true);
  };

  const handleEditArea = (area) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    setAreaForm({
      areaCode: area.areaCode,
      areaName: area.areaName,
      regionId: area.regionId,
      description: area.description || ''
    });
    setEditingArea(area);
    setShowAreaModal(true);
  };

  const handleSaveArea = async () => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (!areaForm.areaName.trim()) {
      alert('Area name is required');
      return;
    }

    try {
      if (editingArea) {
        await updateDoc(doc(db, `companies/${activeCompany.id}/areas`, editingArea.id), {
          ...areaForm,
          updatedAt: serverTimestamp()
        });
      } else {
        await addDoc(collection(db, `companies/${activeCompany.id}/areas`), {
          ...areaForm,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
      setShowAreaModal(false);
      fetchAreas();
    } catch (error) {
      console.error('Error saving area:', error);
      alert('Failed to save area');
    }
  };

  // Keyboard shortcuts: Ctrl+N to add region, Ctrl+S to save active modal
  useDocumentShortcuts({
    onNew: handleAddRegion,
    onSave: showRegionModal ? handleSaveRegion : showAreaModal ? handleSaveArea : undefined,
    viewMode: (showRegionModal || showAreaModal) ? 'form' : 'list',
    saving: false
  });

  const handleDeleteArea = async (area) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    if (window.confirm(`Are you sure you want to delete "${area.areaName}"?`)) {
      try {
        const { hasReferences, message } = await checkEntityReferences(activeCompany?.id, 'area', area.id);
        if (hasReferences) { alert(message); return; }
        await deleteDoc(doc(db, `companies/${activeCompany.id}/areas`, area.id));
        fetchAreas();
      } catch (error) {
        console.error('Error deleting area:', error);
        alert('Failed to delete area');
      }
    }
  };

  const toggleRegion = (regionId) => {
    setExpandedRegions(prev => ({
      ...prev,
      [regionId]: !prev[regionId]
    }));
  };

  const getAreasForRegion = (regionId) => {
    return areas.filter(area => area.regionId === regionId);
  };

  const filteredRegions = regions.filter(reg =>
    reg.regionName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    reg.regionCode.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <PageLayout pageTitle="Regions" breadcrumb="Master Data / Regions">
      <Container>
        <HeaderSection>
          <SearchBox>
            <FiSearch />
            <input
              type="text"
              placeholder="Search regions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <ClearButton onClick={() => setSearchTerm('')}>
                <FiX />
              </ClearButton>
            )}
          </SearchBox>
          {canEdit && (
            <AddButton onClick={handleAddRegion}>
              <FiPlus /> Add Region
            </AddButton>
          )}
        </HeaderSection>

        <StatsRow>
          <StatCard>
            <StatIcon $color={colors.primary.main}><FiMap /></StatIcon>
            <StatInfo>
              <StatValue>{regions.length}</StatValue>
              <StatLabel>Regions</StatLabel>
            </StatInfo>
          </StatCard>
          <StatCard>
            <StatIcon $color={colors.success.main}><FiNavigation /></StatIcon>
            <StatInfo>
              <StatValue>{areas.length}</StatValue>
              <StatLabel>Areas</StatLabel>
            </StatInfo>
          </StatCard>
        </StatsRow>

        {loading ? (
          <LoadingMessage>Loading regions data...</LoadingMessage>
        ) : (
          <TreeContainer>
            {filteredRegions.length === 0 ? (
              <EmptyMessage>
                {searchTerm ? 'No regions found matching your search.' : 'No regions found. Click "Add Region" to create one.'}
              </EmptyMessage>
            ) : (
              filteredRegions.map(region => (
                <RegionNode key={region.id}>
                  <RegionHeader>
                    <RegionLeft onClick={() => toggleRegion(region.id)}>
                      {expandedRegions[region.id] ? <FiChevronDown /> : <FiChevronRight />}
                      <FiMap />
                      <RegionCode>{region.regionCode}</RegionCode>
                      <RegionName>{region.regionName}</RegionName>
                      <RegionCount>({getAreasForRegion(region.id).length} areas)</RegionCount>
                    </RegionLeft>
                    <RegionActions>
                      {canEdit && (
                        <ActionButton onClick={() => handleAddArea(region.id)} title="Add Area">
                          <FiPlus />
                        </ActionButton>
                      )}
                      {canChange && (
                        <ActionButton onClick={() => handleEditRegion(region)} title="Edit">
                          <FiEdit2 />
                        </ActionButton>
                      )}
                      {canDelete && (
                        <ActionButton $danger onClick={() => handleDeleteRegion(region)} title="Delete">
                          <FiTrash2 />
                        </ActionButton>
                      )}
                    </RegionActions>
                  </RegionHeader>

                  {expandedRegions[region.id] && (
                    <AreaList>
                      {getAreasForRegion(region.id).map(area => (
                        <AreaRow key={area.id}>
                          <AreaLeft>
                            <FiNavigation />
                            <AreaCode>{area.areaCode}</AreaCode>
                            <AreaName>{area.areaName}</AreaName>
                          </AreaLeft>
                          <RegionActions>
                            {canChange && (
                              <ActionButton onClick={() => handleEditArea(area)} title="Edit">
                                <FiEdit2 />
                              </ActionButton>
                            )}
                            {canDelete && (
                              <ActionButton $danger onClick={() => handleDeleteArea(area)} title="Delete">
                                <FiTrash2 />
                              </ActionButton>
                            )}
                          </RegionActions>
                        </AreaRow>
                      ))}
                      {getAreasForRegion(region.id).length === 0 && (
                        <EmptyAreaMessage>No areas in this region. Click + to add one.</EmptyAreaMessage>
                      )}
                    </AreaList>
                  )}
                </RegionNode>
              ))
            )}
          </TreeContainer>
        )}
      </Container>

      {/* Region Modal */}
      {showRegionModal && (
        <ModalOverlay>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>{editingRegion ? 'Edit Region' : 'Add Region'}</h3>
              <CloseButton onClick={() => setShowRegionModal(false)}>
                <FiX />
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              <FormGroup>
                <label>Region Code</label>
                <input
                  type="text"
                  value={regionForm.regionCode}
                  onChange={(e) => setRegionForm({ ...regionForm, regionCode: e.target.value })}
                  disabled={editingRegion}
                />
              </FormGroup>
              <FormGroup>
                <label>Region Name *</label>
                <input
                  type="text"
                  value={regionForm.regionName}
                  onChange={(e) => setRegionForm({ ...regionForm, regionName: e.target.value })}
                  placeholder="Enter region name"
                />
              </FormGroup>
              <FormGroup>
                <label>Description</label>
                <textarea
                  value={regionForm.description}
                  onChange={(e) => setRegionForm({ ...regionForm, description: e.target.value })}
                  placeholder="Enter description"
                  rows={3}
                />
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <CancelButton onClick={() => setShowRegionModal(false)}>Cancel</CancelButton>
              {canEdit && (
                <SaveButton onClick={handleSaveRegion}>
                  {editingRegion ? 'Update' : 'Save'}
                </SaveButton>
              )}
            </ModalFooter>
          </Modal>
        </ModalOverlay>
      )}

      {/* Area Modal */}
      {showAreaModal && (
        <ModalOverlay>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>{editingArea ? 'Edit Area' : 'Add Area'}</h3>
              <CloseButton onClick={() => setShowAreaModal(false)}>
                <FiX />
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              <FormGroup>
                <label>Area Code</label>
                <input
                  type="text"
                  value={areaForm.areaCode}
                  onChange={(e) => setAreaForm({ ...areaForm, areaCode: e.target.value })}
                  disabled={editingArea}
                />
              </FormGroup>
              <FormGroup>
                <label>Area Name *</label>
                <input
                  type="text"
                  value={areaForm.areaName}
                  onChange={(e) => setAreaForm({ ...areaForm, areaName: e.target.value })}
                  placeholder="Enter area/city name"
                />
              </FormGroup>
              <FormGroup>
                <label>Parent Region</label>
                <select
                  value={areaForm.regionId}
                  onChange={(e) => setAreaForm({ ...areaForm, regionId: e.target.value })}
                  disabled={!editingArea && selectedRegionId}
                >
                  <option value="">Select Region</option>
                  {regions.map(reg => (
                    <option key={reg.id} value={reg.id}>{reg.regionName}</option>
                  ))}
                </select>
              </FormGroup>
              <FormGroup>
                <label>Description</label>
                <textarea
                  value={areaForm.description}
                  onChange={(e) => setAreaForm({ ...areaForm, description: e.target.value })}
                  placeholder="Enter description"
                  rows={3}
                />
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <CancelButton onClick={() => setShowAreaModal(false)}>Cancel</CancelButton>
              {canEdit && (
                <SaveButton onClick={handleSaveArea}>
                  {editingArea ? 'Update' : 'Save'}
                </SaveButton>
              )}
            </ModalFooter>
          </Modal>
        </ModalOverlay>
      )}
    </PageLayout>
  );
};

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.lg};
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing.md};
  flex-wrap: wrap;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  background: ${colors.background.default};
  border: 1px solid ${colors.border.main};
  border-radius: ${borderRadius.md};
  padding: ${spacing.sm} ${spacing.md};
  flex: 1;
  max-width: 400px;

  svg {
    color: ${colors.text.secondary};
  }

  input {
    border: none;
    outline: none;
    flex: 1;
    font-size: ${typography.fontSize.sm};
    background: transparent;
  }
`;

const ClearButton = styled.button`
  background: transparent;
  color: ${colors.text.secondary};
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${borderRadius.sm};

  &:hover {
    background: ${colors.background.hover};
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.sm} ${spacing.md};
  background: ${colors.primary.main};
  color: ${colors.text.white};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.primary.dark};
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${spacing.md};
`;

const StatCard = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  background: ${colors.background.default};
  padding: ${spacing.md};
  border-radius: ${borderRadius.md};
  border: 1px solid ${colors.border.light};
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${borderRadius.md};
  background: ${props => props.$color}15;
  color: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 24px;
  }
`;

const StatInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatValue = styled.span`
  font-size: ${typography.fontSize.h4};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.text.primary};
`;

const StatLabel = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.secondary};
`;

const TreeContainer = styled.div`
  background: ${colors.background.default};
  border-radius: ${borderRadius.md};
  border: 1px solid ${colors.border.light};
  overflow: hidden;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: ${spacing.xl};
  color: ${colors.text.secondary};
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: ${spacing.xl};
  color: ${colors.text.secondary};
`;

const RegionNode = styled.div`
  border-bottom: 1px solid ${colors.border.light};

  &:last-child {
    border-bottom: none;
  }
`;

const RegionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing.md};
  background: ${colors.background.default};
  transition: background 0.2s ease;

  &:hover {
    background: ${colors.background.hover};
  }
`;

const RegionLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  cursor: pointer;
  flex: 1;

  > svg:first-child {
    color: ${colors.text.secondary};
  }

  > svg:nth-child(2) {
    color: ${colors.primary.main};
  }
`;

const RegionCode = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.secondary};
  font-family: monospace;
  background: ${colors.background.paper};
  padding: 2px 6px;
  border-radius: ${borderRadius.sm};
`;

const RegionName = styled.span`
  font-weight: ${typography.fontWeight.medium};
  color: ${colors.text.primary};
`;

const RegionCount = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.secondary};
`;

const RegionActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background: ${props => props.$danger ? 'transparent' : colors.primary.main + '12'};
  color: ${props => props.$danger ? colors.error.main : colors.primary.main};
  border: 1px solid ${props => props.$danger ? 'transparent' : colors.primary.main + '25'};
  border-radius: ${borderRadius.sm};
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$danger ? colors.error.main + '15' : colors.primary.main + '22'};
    color: ${props => props.$danger ? colors.error.main : colors.primary.dark || colors.primary.main};
  }
`;

const AreaList = styled.div`
  margin-left: ${spacing.lg};
  border-left: 2px solid ${colors.border.light};
  background: ${colors.background.paper};
`;

const AreaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing.sm} ${spacing.md};
  border-bottom: 1px solid ${colors.border.light};
  transition: background 0.2s ease;

  &:hover {
    background: ${colors.background.hover};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const AreaLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};

  > svg {
    color: ${colors.success.main};
  }
`;

const AreaCode = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.secondary};
  font-family: monospace;
  background: ${colors.background.default};
  padding: 2px 6px;
  border-radius: ${borderRadius.sm};
`;

const AreaName = styled.span`
  color: ${colors.text.primary};
  font-size: ${typography.fontSize.sm};
`;

const EmptyAreaMessage = styled.div`
  padding: ${spacing.md};
  text-align: center;
  color: ${colors.text.secondary};
  font-size: ${typography.fontSize.sm};
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${spacing.md};
`;

const Modal = styled.div`
  background: ${colors.background.default};
  border-radius: ${borderRadius.lg};
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${shadows.xl};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${spacing.md} ${spacing.lg};
  border-bottom: 1px solid ${colors.border.light};

  h3 {
    font-size: ${typography.fontSize.lg};
    font-weight: ${typography.fontWeight.semibold};
    color: ${colors.text.primary};
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  color: ${colors.text.secondary};
  border-radius: ${borderRadius.sm};
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.background.hover};
    color: ${colors.text.primary};
  }
`;

const ModalBody = styled.div`
  padding: ${spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};

  label {
    font-size: ${typography.fontSize.sm};
    font-weight: ${typography.fontWeight.medium};
    color: ${colors.text.secondary};
  }

  input, select, textarea {
    padding: ${spacing.sm} ${spacing.md};
    border: 1px solid ${colors.border.main};
    border-radius: ${borderRadius.md};
    font-size: ${typography.fontSize.sm};
    color: ${colors.text.primary};
    background: ${colors.background.default};
    transition: all 0.2s ease;

    &:focus {
      outline: none;
      border-color: ${colors.primary.main};
      box-shadow: 0 0 0 3px ${colors.primary.main}20;
    }

    &:disabled {
      background: ${colors.background.paper};
      cursor: not-allowed;
    }
  }

  textarea {
    resize: vertical;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${spacing.sm};
  padding: ${spacing.md} ${spacing.lg};
  border-top: 1px solid ${colors.border.light};
`;

const CancelButton = styled.button`
  padding: ${spacing.sm} ${spacing.lg};
  background: transparent;
  color: ${colors.text.secondary};
  border: 1px solid ${colors.border.main};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.background.hover};
    border-color: ${colors.text.secondary};
  }
`;

const SaveButton = styled.button`
  padding: ${spacing.sm} ${spacing.lg};
  background: ${colors.primary.main};
  color: ${colors.text.white};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.primary.dark};
  }
`;

export default Regions;
